import { Injectable } from '@angular/core';

export type TotpPendingCredentials = { email: string; password: string };
export type TotpPendingLoginError = { message: string; type?: string };

/**
 * Tab-memory gate for TOTP mid-flow routes (/auth/totp/setup, /auth/totp/verify).
 * Set only after password login routes into TOTP.
 * Cleared on: refresh, leave via browser back, explicit login redirect, successful JWT login.
 * Forward after back cannot reopen TOTP (guard + cleared flag).
 * Login→TOTP does not use replaceUrl (IP-9544) so browser Back returns to login, not prior history.
 * In-app QR ↔ authenticator steps stay on the same component and do not need this to flip.
 *
 * Also holds ephemeral login credentials (memory only) so password-expiry after TOTP
 * can restore the login form like legacy MFA-off / preprod — never written to storage.
 */
@Injectable({
  providedIn: 'root',
})
export class TotpFlowSessionService {
  private active = false;
  private pendingCredentials: TotpPendingCredentials | null = null;
  private pendingLoginError: TotpPendingLoginError | null = null;

  get isActive(): boolean {
    return this.active;
  }

  begin(credentials?: TotpPendingCredentials): void {
    this.active = true;
    if (credentials?.email != null && credentials?.password != null) {
      this.pendingCredentials = {
        email: credentials.email,
        password: credentials.password,
      };
    }
  }

  /** End TOTP mid-flow but keep stashed credentials / pending login error for restore. */
  endFlowKeepCredentials(): void {
    this.active = false;
  }

  clear(): void {
    this.active = false;
    this.pendingCredentials = null;
    this.pendingLoginError = null;
  }

  setPendingLoginError(error: TotpPendingLoginError): void {
    this.pendingLoginError = {
      message: error?.message || '',
      type: error?.type,
    };
  }

  /** One-shot: return and clear stashed credentials. */
  takePendingCredentials(): TotpPendingCredentials | null {
    const creds = this.pendingCredentials;
    this.pendingCredentials = null;
    return creds;
  }

  /** One-shot: return and clear pending login error (e.g. password expired modal). */
  takePendingLoginError(): TotpPendingLoginError | null {
    const err = this.pendingLoginError;
    this.pendingLoginError = null;
    return err;
  }
}
